import React from "react";

import { NAVIGATION_ITEMS, WORKSPACE_TYPES } from "../../config/navigation";
import { hasFeature } from "../../constants/platform";
import { colors, layout, spacing } from "./theme";

const BRAND = {
  name: "Ecovanta",
  logo: "/logo/ecovanta-logo.png"
};

function userHasRequiredRole(user, requiredRoles = []) {
  if (!requiredRoles.length) return true;

  const role = String(user?.role || "").toLowerCase();

  return requiredRoles.includes(role);
}

function userHasRequiredFeature(user, requiredFeature) {
  if (!requiredFeature) return true;

  const plan = String(
    user?.companyPlan ||
      user?.company?.plan ||
      user?.subscription?.planKey ||
      user?.plan ||
      user?.subscriptionPlan ||
      "starter"
  ).toLowerCase();

  return hasFeature(plan, requiredFeature);
}

function getWorkspaceLabel(workspaceType) {
  if (workspaceType === WORKSPACE_TYPES.CUSTOMER) {
    return "Customer Workspace";
  }

  if (workspaceType === WORKSPACE_TYPES.PARTNER) {
    return "Partner Workspace";
  }

  return "Internal Workspace";
}

function getVisibleNavigationItems({ workspaceType, user }) {
  const items =
    NAVIGATION_ITEMS[workspaceType] ||
    NAVIGATION_ITEMS[WORKSPACE_TYPES.INTERNAL] ||
    [];

  return items
    .filter((item) => userHasRequiredRole(user, item.requiredRoles))
    .map((item) => ({
      ...item,
      locked:
        workspaceType === WORKSPACE_TYPES.CUSTOMER &&
        (Boolean(item.comingSoon) ||
          (Boolean(item.requiredFeature) &&
            !userHasRequiredFeature(user, item.requiredFeature)))
    }));
}

export default function Sidebar({
  workspaceType = WORKSPACE_TYPES.INTERNAL,
  user = {
    role: "super_admin",
    plan: "enterprise"
  }
}) {
  const currentPath = window.location.pathname;

  const navItems = getVisibleNavigationItems({
    workspaceType,
    user
  });

  return (
    <aside style={sidebarStyle}>
      <div style={brandStyle}>
        <img src={BRAND.logo} alt={BRAND.name} style={logoStyle} />

        <div>
          <div style={brandNameStyle}>{BRAND.name}</div>
          <div style={workspaceLabelStyle}>
            {getWorkspaceLabel(workspaceType)}
          </div>
        </div>
      </div>

      <nav style={navStyle}>
        {navItems.map((item) => {
          const isActive =
            currentPath === item.href ||
            currentPath.startsWith(`${item.href}/`);

          const content = (
            <>
              <span style={iconStyle}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>

              {item.locked && (
                <span style={lockStyle} title="Upgrade required">
                  🔒
                </span>
              )}
            </>
          );

          if (item.locked) {
            return (
              <a
                key={item.href}
                href={`/app/upgrade?feature=${encodeURIComponent(item.label)}`}
                style={{
                  ...navItemStyle,
                  ...lockedNavItemStyle
                }}
                title={
                  item.comingSoon
                    ? "Coming soon"
                    : "Upgrade required"
                }
              >
                {content}
              </a>
            );
          }

          return (
            <a
              key={item.href}
              href={item.href}
              style={{
                ...navItemStyle,
                ...(isActive ? activeNavItemStyle : {})
              }}
            >
              {content}
            </a>
          );
        })}
      </nav>
    </aside>
  );
}

const sidebarStyle = {
  width: layout.sidebarWidth,
  minHeight: "100vh",
  background: colors.surface,
  borderRight: `1px solid ${colors.border}`,
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 1000
};

const brandStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.md,
  padding: `${spacing.xl}px ${spacing.xl}px`,
  borderBottom: `1px solid ${colors.border}`
};

const logoStyle = {
  width: 48,
  height: 48,
  objectFit: "contain",
  flexShrink: 0
};

const brandNameStyle = {
  fontSize: 20,
  fontWeight: 800,
  color: colors.text,
  letterSpacing: "-0.02em"
};

const workspaceLabelStyle = {
  fontSize: 13,
  color: colors.textMuted,
  marginTop: 4
};

const navStyle = {
  padding: spacing.lg,
  display: "grid",
  gap: spacing.sm
};

const navItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.md,
  padding: `${spacing.md}px ${spacing.lg}px`,
  borderRadius: 10,
  color: colors.textMuted,
  textDecoration: "none",
  fontWeight: 700
};

const activeNavItemStyle = {
  background: colors.primaryLight,
  color: colors.primaryDark
};

const lockedNavItemStyle = {
  opacity: 0.55,
  cursor: "not-allowed",
  background: colors.surfaceMuted
};

const iconStyle = {
  width: 22,
  display: "inline-flex",
  justifyContent: "center"
};

const lockStyle = {
  fontSize: 12
};